import { router, usePage } from "@inertiajs/react"
import { ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon, MapPinIcon } from "lucide-react"
import { type ReactNode, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { AdminLayout } from "@/layouts/AdminLayout"
import type {
  EventReviewData,
  ModerationReviewProps,
  ModerationReviewRecord,
  PoiReviewData,
} from "@/lib/types"
import type { SharedProps } from "@/types"

interface Props {
  review:           ModerationReviewProps
  subject:          PoiReviewData | EventReviewData
  previous_reviews: ModerationReviewRecord[]
  urls:             { index: string; decide: string; next_pending: string | null }
}

function statusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    pending:       "secondary",
    needs_changes: "outline",
    approved:      "default",
    rejected:      "destructive",
  }
  return <Badge variant={variants[status] ?? "outline"}>{status.replace(/_/g, " ")}</Badge>
}

function AuditHistory({ reviews }: { reviews: ModerationReviewRecord[] }) {
  const [open, setOpen] = useState(false)
  const past = reviews.filter((r) => r.decision !== "submitted")
  if (past.length === 0) return null

  return (
    <div className="mt-4">
      <button
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronUpIcon className="size-4" /> : <ChevronDownIcon className="size-4" />}
        Previous reviews ({past.length})
      </button>
      {open && (
        <div className="mt-2 space-y-2 rounded-md border p-3 text-sm">
          {past.map((r) => (
            <div key={r.id} className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                {statusBadge(r.decision)}
                <span className="text-muted-foreground">
                  by {r.reviewed_by ?? "unknown"} ·{" "}
                  {new Date(r.reviewed_at).toLocaleDateString()}
                </span>
              </div>
              {r.notes && <p className="ml-0.5 text-muted-foreground">{r.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SubjectPreview({ subject }: { subject: PoiReviewData | EventReviewData }) {
  const isPoi = subject.type === "Poi"
  const poi   = isPoi ? (subject as PoiReviewData) : null

  return (
    <div className="space-y-4">
      {/* Photo carousel or placeholder */}
      {poi && poi.media.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {poi.media.map((m, i) => (
            <img
              key={i}
              src={m.url}
              alt={m.alt ?? subject.name}
              className="h-48 w-72 shrink-0 rounded-md object-cover"
            />
          ))}
        </div>
      ) : (
        <div className="flex h-36 items-center justify-center rounded-md bg-muted text-sm text-muted-foreground">
          No photos
        </div>
      )}

      {/* Name + status */}
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">{subject.name}</h1>
        {statusBadge(subject.status)}
        <Badge variant="outline" className="font-mono text-xs">
          {subject.type === "Poi" ? "POI" : "Event"}
        </Badge>
      </div>

      {/* Region + submitter */}
      <p className="text-sm text-muted-foreground">
        {subject.region && <span>Region: {subject.region} · </span>}
        Submitted by {subject.submitted_by ?? "unknown"}
      </p>

      {/* Description */}
      {subject.description && (
        <p className="text-sm leading-relaxed">{subject.description}</p>
      )}

      {/* POI-specific fields */}
      {poi && (
        <>
          {/* Categories */}
          {poi.categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {poi.categories.map((c) => (
                <Badge key={c.id} variant="secondary">
                  {c.icon && <span className="mr-1">{c.icon}</span>}
                  {c.name}
                </Badge>
              ))}
            </div>
          )}

          {/* Location */}
          {(poi.address || (poi.lat && poi.lng)) && (
            <div className="flex items-start gap-1.5 text-sm">
              <MapPinIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div>
                {poi.address && <p>{poi.address}</p>}
                {poi.lat && poi.lng && (
                  <p className="text-xs text-muted-foreground">
                    {poi.lat.toFixed(5)}, {poi.lng.toFixed(5)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Contact + links */}
          <div className="flex flex-wrap gap-3 text-sm">
            {poi.phone && (
              <a href={`tel:${poi.phone}`} className="text-muted-foreground hover:text-foreground">
                {poi.phone}
              </a>
            )}
            {poi.website_url && (
              <a
                href={poi.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
              >
                Website <ExternalLinkIcon className="size-3" />
              </a>
            )}
            {poi.booking_url && (
              <a
                href={poi.booking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
              >
                Booking <ExternalLinkIcon className="size-3" />
              </a>
            )}
            {poi.google_maps_url && (
              <a
                href={poi.google_maps_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
              >
                Google Maps <ExternalLinkIcon className="size-3" />
              </a>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default function ModerationReview({ review, subject, previous_reviews, urls }: Props) {
  const { errors } = usePage<SharedProps>().props
  const [decision, setDecision]     = useState("")
  const [notes, setNotes]           = useState("")
  const [processing, setProcessing] = useState(false)
  const notesRequired = decision === "rejected" || decision === "needs_changes"

  function submitDecision(chosen: string) {
    if ((chosen === "rejected" || chosen === "needs_changes") && !notes.trim()) {
      setDecision(chosen)
      return
    }
    setProcessing(true)
    router.post(
      urls.decide,
      { decision: chosen, notes },
      { onFinish: () => setProcessing(false) }
    )
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!decision) return
    submitDecision(decision)
  }

  return (
    <div>
      {/* Back link */}
      <div className="mb-4">
        <button
          type="button"
          className="text-sm text-muted-foreground hover:text-foreground"
          onClick={() => router.visit(urls.index)}
        >
          ← Back to queue
        </button>
      </div>

      <div className="flex gap-8">
        {/* Left: Preview (60%) */}
        <div className="min-w-0 flex-[3]">
          <SubjectPreview subject={subject} />
          <Separator className="my-4" />
          <AuditHistory reviews={previous_reviews} />
        </div>

        {/* Right: Decision form (40%) */}
        <div className="w-80 shrink-0">
          <div className="rounded-lg border p-5">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Decision
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Three decision buttons */}
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant={decision === "approved" ? "default" : "outline"}
                  className="justify-start bg-green-600 text-white hover:bg-green-700 aria-pressed:ring-2"
                  aria-pressed={decision === "approved"}
                  onClick={() => setDecision("approved")}
                  disabled={processing}
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  variant={decision === "needs_changes" ? "secondary" : "outline"}
                  className="justify-start border-amber-300 text-amber-700 hover:bg-amber-50 aria-pressed:bg-amber-100"
                  aria-pressed={decision === "needs_changes"}
                  onClick={() => setDecision("needs_changes")}
                  disabled={processing}
                >
                  Needs Changes
                </Button>
                <Button
                  type="button"
                  variant={decision === "rejected" ? "destructive" : "outline"}
                  className="justify-start"
                  aria-pressed={decision === "rejected"}
                  onClick={() => setDecision("rejected")}
                  disabled={processing}
                >
                  Reject
                </Button>
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <Label htmlFor="notes">
                  Notes
                  {notesRequired && <span className="ml-1 text-destructive">*</span>}
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={
                    notesRequired
                      ? "Required — explain what needs to change"
                      : "Optional — visible to the submitter"
                  }
                  rows={4}
                  aria-required={notesRequired}
                />
                {notesRequired && !notes.trim() && (
                  <p className="text-xs text-destructive">
                    Notes are required for this decision.
                  </p>
                )}
                {errors.notes && (
                  <p className="text-xs text-destructive">{errors.notes}</p>
                )}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={processing || !decision || (notesRequired && !notes.trim())}
                className="w-full"
              >
                {processing ? "Saving…" : "Confirm decision"}
              </Button>

              {/* Next item shortcut */}
              {urls.next_pending && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => router.visit(urls.next_pending!)}
                  disabled={processing}
                >
                  Skip to next →
                </Button>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

ModerationReview.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
